// ============================================
// HEROSHHI — "Check your email": the 6-digit code that finishes sign-up
// File: src/pages/auth/EmailCodeStep.jsx
// Shown after sign-up, and when a new account that never confirmed tries to sign in.
// ============================================
import React, { useState, useEffect } from 'react';
import config from '../../config';
import AuthLayout, { Field, Notice, SubmitButton } from './AuthLayout';

export default function EmailCodeStep({ email, onVerified, onBack, backLabel = 'Back' }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [wait, setWait] = useState(60);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setVerifying(true); setError(''); setInfo('');
    try {
      const r = await fetch(`${config.API_URL}/api/auth/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, code })
      });
      const d = await r.json();
      if (d.success && d.token) { onVerified(d.user, d.token); return; }
      setError(d.message || 'That code is not right. Check it and try again.');
    } catch (err) {
      setError('We couldn\'t reach Heroshhi. Check your connection and try again.');
    }
    setVerifying(false);
  };

  const resend = async () => {
    setError(''); setInfo('');
    try {
      const r = await fetch(`${config.API_URL}/api/auth/resend-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email })
      });
      const d = await r.json();
      if (d.success) { setInfo('A new code is on its way. It can take a minute.'); setWait(60); setCode(''); }
      else { setError(d.message || 'We couldn\'t send a new code. Please try again.'); if (d.wait) setWait(d.wait); }
    } catch (err) {
      setError('We couldn\'t reach Heroshhi. Check your connection and try again.');
    }
  };

  return (
    <AuthLayout>
      {onBack && <button type="button" className="hs-back hs-link-btn" style={{ textDecoration: 'none' }} onClick={onBack}>{backLabel}</button>}
      <h1>Check your email</h1>
      <p className="hs-auth-sub">We sent a 6-digit code to:</p>
      <span className="hs-email-chip">{email}</span>
      {error && <Notice>{error}</Notice>}
      {info && <Notice kind="ok">{info}</Notice>}
      <form onSubmit={verify} noValidate>
        <Field id="email-code" label="Code">
          <input
            id="email-code"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            placeholder="000000"
            className="hs-input hs-code"
            autoFocus
            autoComplete="one-time-code"
          />
        </Field>
        <SubmitButton loading={verifying} loadingText="Checking" disabled={code.length !== 6}>Continue</SubmitButton>
      </form>
      <p className="hs-auth-foot">
        {wait > 0
          ? <>You can ask for a new code in {wait} s. Not arrived? Look in your spam folder.</>
          : <><button type="button" className="hs-link-btn" onClick={resend}>Send a new code</button>. Not arrived? Look in your spam folder.</>}
      </p>
    </AuthLayout>
  );
}
